import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { getFilterFromLocalStorage } from '@highlander/services/helpers';
import { ProductRequest, RequestsToggleOptions } from '@models/products-and-orders/requests';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { REQUESTS_TABLE_ID } from '../../../app-pages/products-orders/requests/requests.component';
import { createCustomStateSetOnUrlHandler, getAll, getOne, StateDictionary } from '../../helpers';
import { RequestsService } from './requests.service';

type RequestsState = {
  requests: ProductRequest[];
  isLoading: boolean;
  selectedRequest: ProductRequest | null;
  isSelectedRequestLoading: boolean;
  toggleOption: RequestsToggleOptions;
};

const initialState: RequestsState = {
  requests: [],
  isLoading: false,
  selectedRequest: null,
  isSelectedRequestLoading: false,
  toggleOption: RequestsToggleOptions.Active,
};

const requestsDictionary: StateDictionary<ProductRequest> = {
  entityList: 'requests',
  listLoading: 'isLoading',
  selectedEntity: 'selectedRequest',
  selectedEntityLoading: 'isSelectedRequestLoading',
  entityId: 'id',
  createLoading: '',
  sortFn: (a, b) => {
    const dateA = a.lastUpdatedBy?.date || a.createdBy.date;
    const dateB = b.lastUpdatedBy?.date || b.createdBy.date;
    return dateA < dateB ? 1 : -1;
  },
};

export const RequestsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('requests-store'),

  withComputed(state => ({
    selectedRequestId: computed(() => state.selectedRequest()?.id || null),
    selectedExternalProductId: computed(() => state.selectedRequest()?.product?.externalId || null),
  })),

  withMethods((store, requestsService = inject(RequestsService), router = inject(Router)) => {
    createCustomStateSetOnUrlHandler<RequestsState>('/requests', { selectedRequest: null })(store);
    createCustomStateSetOnUrlHandler<RequestsState>('/empowered-licenses', { selectedRequest: null })(store);

    const savedFilter = getFilterFromLocalStorage(REQUESTS_TABLE_ID);
    if (savedFilter?.toggleSearch) {
      patchState(store, {
        toggleOption: savedFilter.toggleSearch === 'Active' ? RequestsToggleOptions.Active : RequestsToggleOptions.All,
      });
    }

    const repositoryMethods = {
      getAll: getAll<ProductRequest, boolean | undefined>({
        store,
        service: requestsService,
        dictionary: requestsDictionary,
      }),

      getOne: getOne<ProductRequest>({
        store,
        service: requestsService,
        dictionary: requestsDictionary,
        onSuccessCallback: entity => {
          const current = store.requests();
          if (!current.find(r => r.id === entity.id)) {
            const updated = requestsDictionary.sortFn ? [...current, entity].sort(requestsDictionary.sortFn) : [...current, entity];
            patchState(store, { requests: updated });
          }
        },
      }),
    };

    const navigationActions = {
      goToRequestActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['requests', id]);

        if (!result) return false;

        if (!store.isSelectedRequestLoading()) repositoryMethods.getOne(id);

        return true;
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,

      loadRequests: (toggle?: RequestsToggleOptions) => {
        if (toggle !== undefined) {
          patchState(store, { toggleOption: toggle });
        }
        const currentToggle = store.toggleOption();
        repositoryMethods.getAll(currentToggle === RequestsToggleOptions.Active ? true : undefined);
      },

      updateToggleOption: (option: RequestsToggleOptions) => {
        patchState(store, { toggleOption: option });
      },
    };
  }),
);

export interface RequestsStoreType {
  readonly requests: Signal<ProductRequest[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedRequest: Signal<ProductRequest | null>;
  readonly isSelectedRequestLoading: Signal<boolean>;
  readonly toggleOption: Signal<RequestsToggleOptions>;

  readonly selectedRequestId: Signal<string | null>;
  readonly selectedExternalProductId: Signal<string | null>;

  readonly getAll: (toggle?: boolean) => void;
  readonly getOne: (id: string) => void;
  readonly goToRequestActions: (id: string) => Promise<boolean>;
  readonly loadRequests: (toggle?: RequestsToggleOptions) => void;
  readonly updateToggleOption: (option: RequestsToggleOptions) => void;
}
